import React, { useCallback, useEffect, useRef } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import '../../../../Styles/components/reactPhoneNumberInput.scss';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { isValidInternationalPhoneNumber, getInvalidMobilePrefix } from '../../../../utils/phoneUtils';

export const PhoneNumberInputText = ({
    fromSubscriberImport,
    getFormErrorMessage,
    formik,
    phoneNo,
    isFormFieldValid,
    defaultCountry,
    label,
    hint,
    disabled,
    placeholder,
    containerClassName = 'invite-number',
    errorClassName = 'invitation_errors',
    errorMessages,
}) => {
    const { t } = useTranslation('common');
    const formikRef = useRef(formik);
    formikRef.current = formik;

    const requiredMessage = errorMessages?.required ?? t("invitation.number_error");
    const invalidMessage = errorMessages?.invalid ?? t("invitation.invalid_number");

    const getInvalidPrefixMessage = useCallback((phone, dialCode, countryCode) => {
        const invalidPrefix = getInvalidMobilePrefix(phone, dialCode, countryCode);
        if (!invalidPrefix) return '';
        return t("invitation.invalid_mobile_prefix", invalidPrefix);
    }, [t]);

    const validatePhone = useCallback((phone, dialCode, countryCode) => {
        if (!phone || phone.trim() === '') {
            formikRef.current.setFieldError('number', requiredMessage);
            return false;
        }

        const prefixMessage = getInvalidPrefixMessage(phone, dialCode, countryCode);
        if (prefixMessage) {
            formikRef.current.setFieldError('number', prefixMessage);
            return false;
        }

        if (!isValidInternationalPhoneNumber(phone, dialCode, countryCode)) {
            formikRef.current.setFieldError('number', invalidMessage);
            return false;
        }

        formikRef.current.setFieldError('number', undefined);
        return true;
    }, [requiredMessage, invalidMessage, getInvalidPrefixMessage]);

    const handlePhoneChange = useCallback((phone, { country } = {}) => {
        const dialCode = country?.dialCode || '';
        const countryCode = (country?.iso2 || defaultCountry || '').toUpperCase();

        formik.setFieldValue('number', phone);
        formik.setFieldValue('dialCode', dialCode);
        formik.setFieldValue('countryCode', countryCode);
        validatePhone(phone, dialCode, countryCode);
    }, [defaultCountry, formik, validatePhone]);

    const handleBlur = useCallback(() => {
        formik.setFieldTouched('number', true);
        validatePhone(formik.values.number, formik.values.dialCode, formik.values.countryCode);
    }, [formik, validatePhone]);

    useEffect(() => {
        if (formik.values.number) {
            validatePhone(formik.values.number, formik.values.dialCode, formik.values.countryCode || defaultCountry);
        }
    }, [defaultCountry, formik.values.countryCode, formik.values.dialCode, formik.values.number, validatePhone]);

    if (fromSubscriberImport) return null;

    const countryCode = (formik.values.countryCode || defaultCountry || '').toUpperCase();
    const prefixWarning = getInvalidPrefixMessage(
        formik.values.number,
        formik.values.dialCode,
        countryCode
    );
    const phoneWarning = prefixWarning || getFormErrorMessage('number');
    const showPhoneInvalid = Boolean(prefixWarning) || isFormFieldValid('number');

    return (
        <div>
            {label ?? (
                <div className="title flex mt-4">
                    <p className="labels">{t('invitation.subscriber_number')}</p>
                    <p className="isterik">*</p>
                </div>
            )}
            <div className={containerClassName}>
                <PhoneInput
                    name="number"
                    defaultCountry={defaultCountry || 'gh'}
                    value={formik.values.number || ''}
                    onChange={handlePhoneChange}
                    onBlur={handleBlur}
                    placeholder={placeholder ?? t('invitation.number')}
                    disabled={disabled ?? !!phoneNo}
                    inputClassName={classNames('phone-input', {
                        'p-invalid': showPhoneInvalid,
                    })}
                    className="phone-input-container"
                />
                <div className={classNames('phone-error-message', errorClassName)}>
                    {phoneWarning}
                </div>
            </div>
            {hint}
        </div>
    );
};
